"use client";

import React from "react";
import { Loader, House } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function UpgradeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-10 px-6">
        <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" style={{
        background: "var(--primary)",
        color: "var(--primary-foreground)"
      }}>
          <House className="size-8" />
        </div>
        <p className="text-xl font-default font-semibold mb-2" style={{
        color: "var(--primary)"
      }}>
          {successMessage}
        </p>
        <p className="text-sm mb-6" style={{
        color: "var(--muted-foreground)"
      }}>
          You're on the list! We'll notify you as soon as a matching property hits the market.
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-accent rounded-md">
          Set Another Alert
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem data-index={0}>
              <FormLabel style={{
          color: "var(--muted-foreground)"
        }} className="text-xs uppercase tracking-widest font-default font-semibold">
                Full Name
              </FormLabel>
              <FormControl>
                <Input type="text" placeholder="Jane Doe" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs" style={{
          color: "var(--muted-foreground)"
        }}>
                Enter your legal name.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem data-index={1}>
              <FormLabel style={{
          color: "var(--muted-foreground)"
        }} className="text-xs uppercase tracking-widest font-default font-semibold">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs" style={{
          color: "var(--muted-foreground)"
        }}>
                For instant email notifications.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem data-index={2}>
              <FormLabel style={{
          color: "var(--muted-foreground)"
        }} className="text-xs uppercase tracking-widest font-default font-semibold">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(512) 555-0123" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs" style={{
          color: "var(--muted-foreground)"
        }}>
                For SMS listing alerts.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="primary_city" rules={{
          required: "Primary city is required"
        }} render={({
          field
        }) => <FormItem data-index={3}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  Primary City
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Austin" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Primary city for alerts.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="primary_state" rules={{
          required: "State is required"
        }} render={({
          field
        }) => <FormItem data-index={4}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  State
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="TX" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  State code.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="second_city" render={({
          field
        }) => <FormItem data-index={5}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  Secondary City
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Dallas" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Optional secondary city.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="second_state" render={({
          field
        }) => <FormItem data-index={6}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  State
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="TX" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Optional state.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="third_city" render={({
          field
        }) => <FormItem data-index={7}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  Third City
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Houston" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Optional third city.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="third_state" render={({
          field
        }) => <FormItem data-index={8}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  State
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="TX" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Optional state.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="min_down_payment" render={({
          field
        }) => <FormItem data-index={9}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  Min Down Payment
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="$5,000" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Minimum amount you can pay upfront.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="max_down_payment" render={({
          field
        }) => <FormItem data-index={10}>
                <FormLabel style={{
            color: "var(--muted-foreground)"
          }} className="text-xs uppercase tracking-widest font-default font-semibold">
                  Max Down Payment
                </FormLabel>
                <FormControl>
                  <Input type="text" placeholder="$100,000" className="bg-input/20 border-border text-foreground placeholder:text-muted-foreground focus-visible:border-primary rounded-md" {...field} />
                </FormControl>
                <FormDescription className="text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                  Maximum amount you can pay upfront.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 rounded-md border text-sm" style={{
      background: "var(--destructive)/15",
      borderColor: "var(--destructive)",
      color: "var(--destructive)"
    }}>
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full mt-2 font-default font-bold text-base rounded-md shadow-lg" style={{
      background: "var(--primary)",
      color: "var(--primary-foreground)"
    }}>
        {isSubmitting ? <>
            <Loader className="animate-spin size-4" />
            Setting Alert...
          </> : "Notify Me"}
      </Button>
    </Form>;
}
export default function DonTMissOut() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="249098" id="dont-miss-out" className="dark bg-background text-foreground relative overflow-hidden py-20 px-4">
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 80% 50% at 50% 110%, var(--primary) 0%, transparent 65%)",
      opacity: 0.08
    }} />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-1 rounded-lg pointer-events-none" style={{
      background: "var(--primary)",
      boxShadow: "0 0 80px 30px var(--primary)",
      opacity: 0.2
    }} />

      <div className="relative z-10 max-w-2xl mx-auto">
        <div ref={ref} className={`rounded-lg border p-8 md:p-12 shadow-4xl transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        background: "var(--card)",
        borderColor: "var(--border)"
      }}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border text-xs font-default font-bold uppercase tracking-widest" style={{
            borderColor: "var(--primary)",
            color: "var(--primary)",
            background: "var(--accent)"
          }}>
              <House className="size-3.5" />
              LISTING ALERTS
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-default font-bold text-3xl md:text-4xl leading-tight text-foreground mb-3 tracking-tight">
              Don't Miss Your{" "}
              <span style={{
              color: "var(--primary)"
            }}>Dream House</span>
            </h2>
            <p className="text-base md:text-lg font-default" style={{
            color: "var(--muted-foreground)"
          }}>
              Be the first to know when new owner‑financed properties across the country match your budget.
            </p>
          </div>

          <FormProvider formKey="dont_miss_out_form_find_home" formId="dont_miss_out_form_find_home" sectionName="dont-miss-out-cta" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          primary_city: "",
          primary_state: "",
          second_city: "",
          second_state: "",
          third_city: "",
          third_state: "",
          min_down_payment: "",
          max_down_payment: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          primary_city: {
            required: true
          },
          primary_state: {
            required: true
          },
          second_city: {},
          second_state: {},
          third_city: {},
          third_state: {},
          min_down_payment: {},
          max_down_payment: {}
        }} submitText="Notify Me" successMessage="Alert set! You're now on the priority list." errorMessage="Something went wrong. Please try again.">
            <UpgradeForm />
          </FormProvider>

          <p className="text-center text-xs mt-6 font-default" style={{
          color: "var(--muted-foreground)"
        }}>
            Instant SMS & Email alerts. No credit check required to browse.
          </p>
        </div>
      </div>
    </section>;
}